/**
 * Reports / MIS read-view client (Inc 2). Read-only firm-wide aggregations from
 * the backend cube (routers/reports.py): AuM + realized revenue per month, and
 * the current run-rate. Admin + Compliance only (enforced server-side).
 */
import { api } from './client';

export interface MonthlyPoint {
  period_month: string;          // YYYY-MM-01
  aum_cents: number;
  fee_net_cents: number;
  contributing_accounts: number | null;  // null for historical months (agreement-grain)
  contributing_agreements: number;
  fee_rate_bps: number | null;   // annualized avg fee%: realized x12 (<cut-off) / run-rate (>=cut-off)
}

export interface AumRevenueSeries {
  period_from: string;
  period_to: string;
  points: MonthlyPoint[];
}

export interface RunRate {
  annual_fee_cents: number;
  aum_basis_cents: number;
  effective_rate_bps: number | null;
  agreements_counted: number;
  accounts_counted: number;
}

/** `from` / `to` are YYYY-MM-01 date strings (first-of-month). */
export function getAumRevenueSeries(from: string, to: string): Promise<AumRevenueSeries> {
  return api.get<AumRevenueSeries>(
    `/reports/aum-revenue-series?period_from=${from}&period_to=${to}`,
  );
}

export function getRunRate(): Promise<RunRate> {
  return api.get<RunRate>('/reports/run-rate');
}

export interface FirmMoneyMultiple {
  multiple: number | null;          // AuM(end) ÷ (AuM entering period + net flows); null when not meaningful
  invested_cents: number;           // AuM entering the period + Σ net flows during it (denominator)
  current_aum_cents: number | null; // AuM at period end (= current AuM; numerator)
  fidelity: 'measured' | 'estimated';  // 'estimated' if any flow month was inferred/gap
  not_meaningful: boolean;          // denominator ≤ floor → multiple null (rendered 'n/m')
}

/** Firm-wide money multiple over the selected period (window-aware, net-flow based). */
export function getFirmMoneyMultiple(from: string, to: string): Promise<FirmMoneyMultiple> {
  return api.get<FirmMoneyMultiple>(`/reports/money-multiple?period_from=${from}&period_to=${to}`);
}

export interface DecompositionPoint {
  period_month: string;          // YYYY-MM-01
  nnb_cents: number;             // net new business = inflow − outflow
  mm_cents: number | null;       // market movement = ΔAuM − NNB; null if AuM unavailable
  delta_aum_cents: number | null;
  fidelity?: 'measured' | 'inferred' | 'gap';  // worst NNB rung among contributing agreements
}

export interface AumDecomposition {
  period_from: string;
  period_to: string;
  points: DecompositionPoint[];
}

export function getAumDecomposition(from: string, to: string): Promise<AumDecomposition> {
  return api.get<AumDecomposition>(
    `/reports/aum-decomposition?period_from=${from}&period_to=${to}`,
  );
}

export interface AgreementPricePoint {
  agreement_id: string;
  label: string;                 // code_1, e.g. "CIIF-301"
  mnemonic: string | null;       // client_mnemonic, e.g. "HELGOM" — tooltips show both
  aum_cents: number;             // current priced AuM
  fee_bps: number;               // effective annual rate = annual fee / AuM (bps)
  annual_fee_cents: number;      // run-rate revenue (Σ per-account annual fee)
  last_data_month: string | null; // 'YYYY-MM-DD' — that client's most recent data month
}

export interface PriceVsSize {
  points: AgreementPricePoint[];
}

/** Point-in-time (no period): one point per book agreement. */
export function getPriceVsSize(): Promise<PriceVsSize> {
  return api.get<PriceVsSize>('/reports/price-vs-size');
}

export interface RmLeagueRow {
  rm_id: string;
  rm_name: string;
  agreements_counted: number;
  annual_fee_cents: number;
  aum_basis_cents: number;
  effective_rate_bps: number | null;
}

export interface RmLeague {
  rows: RmLeagueRow[];             // ranked by AuM descending (server-side)
}

/** Point-in-time per-RM league. Shared agreements count toward each named RM. */
export function getRmLeague(): Promise<RmLeague> {
  return api.get<RmLeague>('/reports/rm-league');
}

export interface DataSpanResponse {
  first_month: string | null;     // 'YYYY-MM-DD' (first-of-month) or null when no data
  last_month: string | null;      // 'YYYY-MM-DD' — the data horizon
}

/** Earliest + latest month with AuM data — anchors the period windows. Firm-wide
 * (the book) by default; pass an agreementId for that one agreement's own span. */
export function getDataSpan(agreementId?: string): Promise<DataSpanResponse> {
  const q = agreementId ? `?agreement_id=${agreementId}` : '';
  return api.get<DataSpanResponse>(`/reports/data-span${q}`);
}
